<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col class="align-center d-flex" cols="10">
					<v-toolbar-title>{{ $t('add-labs-header') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>

		<v-card-text :key="key" class="pt-4 pb-0 overflow-y-auto" style="height: calc(100vh - 205px)">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-select
						v-model="reviewedBy"
						:label="$t('reviewed-by')"
						:items="medicalStaff"
						item-text="fullName"
						outlined
						dense
						hide-details
						return-object
						:menu-props="{ maxHeight: 200, offsetY: true }">
						<template v-slot:item="{ item }">
							<span style="width: 100%">{{ item.fullName }} </span>
						</template>
					</v-select>
				</v-col>

				<v-col :cols="selectedLabSpecimenType === 'Other' ? 6 : 12">
					<v-select
						v-model="selectedLabSpecimenType"
						:label="$t('specimen-type')"
						:items="labSpecimenTypeTemplate"
						item-text="name"
						outlined
						dense
						hide-details
						:menu-props="{ maxHeight: 200, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ item.name }} </span>
								</template>
								<span>{{ item.description }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col v-if="selectedLabSpecimenType === 'Other'" cols="6">
					<v-text-field v-model="otherLabSpecimenType" :label="$t('specimen-type')" outlined dense hide-details />
				</v-col>

				<v-col :cols="selectedLabPanelGroup === 'Other' ? 6 : 12">
					<v-select
						v-model="selectedLabPanelGroup"
						:label="$t('panel-group')"
						:items="labPanelGroupTemplate"
						item-text="name"
						outlined
						dense
						hide-details
						return-object
						@change="selectedLabTestName = ''"
						:menu-props="{ maxHeight: 200, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ item.name }} </span>
								</template>
								<span>{{ item.description }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col v-if="selectedLabPanelGroup === 'Other'" cols="6">
					<v-text-field v-model="otherLabPanelGroup" :label="$t('panel-group')" outlined dense hide-details />
				</v-col>

				<v-col
					:cols="selectedLabPanelGroup === 'Other' ? 6 : 12"
					v-if="selectedLabPanelGroup.name !== 'Other' && selectedLabPanelGroup !== ''">
					<v-select
						v-model="selectedLabTestName"
						:label="$t('test')"
						:items="selectedLabPanelGroup.tests"
						item-text="name"
						outlined
						dense
						hide-details
						:menu-props="{ maxHeight: 200, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ item.name }} </span>
								</template>
								<span>{{ item.description }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col v-if="selectedLabPanelGroup.name === 'Other'" cols="12">
					<v-text-field v-model="selectedLabTestName" :label="$t('test')" outlined dense hide-details />
				</v-col>

				<v-col :cols="selectedLabUnit === 'Other' ? 4 : 6">
					<v-text-field v-model="value" :label="$t('value')" outlined dense hide-details></v-text-field>
				</v-col>

				<v-col :cols="selectedLabUnit === 'Other' ? 4 : 6">
					<v-select
						v-model="selectedLabUnit"
						:label="$t('unit')"
						:items="labUnitTemplate"
						item-text="name"
						outlined
						dense
						hide-details
						:menu-props="{ maxHeight: 200, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ item.name }} </span>
								</template>
								<span>{{ item.description }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col v-if="selectedLabUnit === 'Other'" cols="4">
					<v-text-field v-model="otherLabUnit" :label="$t('unit')" outlined dense hide-details />
				</v-col>

				<v-col :cols="selectedLabInterpretation === 'Other' ? 4 : 9">
					<v-select
						v-model="selectedLabInterpretation"
						:items="labInterpretationTemplate"
						item-text="name"
						:label="$t('interpretation')"
						outlined
						dense
						hide-details
						:menu-props="{ maxHeight: 200, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ item.name }} </span>
								</template>
								<span>{{ item.description }}</span>
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col v-if="selectedLabInterpretation === 'Other'" cols="5">
					<v-text-field v-model="otherLabInterpretation" :label="$t('interpretation')" outlined dense hide-details />
				</v-col>

				<v-col cols="3" class="d-flex justify-center">
					<v-checkbox v-model="isCritical" :label="$t('is-critical')" dense hide-details></v-checkbox>
				</v-col>

				<v-col cols="12">
					<v-text-field
						v-model="relatedDiagnosisCode"
						:label="$t('related-diagnosis-code')"
						outlined
						dense
						hide-details />
				</v-col>

				<!-- <v-col :cols="selectedLabSource === 'Other' ? 6 : 12"> -->
				<!-- 	<v-select -->
				<!-- 		v-model="selectedLabSource" -->
				<!-- 		:label="$t('lab-source')" -->
				<!-- 		:items="labSourceTemplate" -->
				<!-- 		item-text="name" -->
				<!-- 		outlined -->
				<!-- 		dense -->
				<!-- 		hide-details -->
				<!-- 		:menu-props="{ maxHeight: 200, offsetY: true }"> -->
				<!-- 		<template v-slot:item="{ item }"> -->
				<!-- 			<v-tooltip left> -->
				<!-- 				<template v-slot:activator="{ on, attrs }"> -->
				<!-- 					<span v-bind="attrs" v-on="on" style="width: 100%">{{ item.name }} </span> -->
				<!-- 				</template> -->
				<!-- 				<span>{{ item.description }}</span> -->
				<!-- 			</v-tooltip> -->
				<!-- 		</template> -->
				<!-- 	</v-select> -->
				<!-- </v-col> -->
				<!-- <v-col v-if="selectedLabSource === 'Other'" cols="6"> -->
				<!-- 	<v-text-field v-model="otherLabSource" :label="$t('lab-source')" outlined dense hide-details /> -->
				<!-- </v-col> -->

				<v-col cols="12">
					<v-menu
						v-model="dateMenu"
						:close-on-content-click="false"
						transition="scale-transition"
						offset-y
						min-width="auto">
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="formattedDate"
								:label="$t('lab-date')"
								prepend-inner-icon="mdi-calendar"
								readonly
								outlined
								dense
								hide-details
								v-bind="attrs"
								v-on="on" />
						</template>
						<v-date-picker v-model="labDate" no-title @input="dateMenu = false" />
					</v-menu>
				</v-col>
			</v-row>
		</v-card-text>

		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12" class="mx-0">
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:inputLabel="$t('time-spent')" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip top v-if="!addButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('add') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="addLab()" color="dark_purple" style="color: white" block>
						{{ $t('add') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { getRowsForDataTable, formatTimestamp } from '../../utils/utilities';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMLabsAdd',
	components: { InputTimeFormatter },
	data () {
		return {
			key: false,
			medicalStaff: null,
			timeSpentSeconds: 0,
			reviewedBy: null,
			value: '',
			isCritical: false,
			relatedDiagnosisCode: '',

			labPanelGroupTemplate: null,
			labSpecimenTypeTemplate: null,
			labInterpretationTemplate: null,
			labUnitTemplate: null,
			// labSourceTemplate: null,

			selectedLabTestName: '',
			selectedLabPanelGroup: '',
			selectedLabSpecimenType: '',
			selectedLabInterpretation: '',
			selectedLabUnit: '',
			// selectedLabSource: '',

			otherLabPanelGroup: '',
			otherLabSpecimenType: '',
			otherLabInterpretation: '',
			otherLabUnit: '',
			// otherLabSource: '',

			dateMenu: false,
			labDate: new Date().toISOString().substring(0, 10),
		};
	},
	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			roleList: (state) => state.roles.roles,
		}),

		addButtonEnabled () {
			return (
				this.timeSpentSeconds &&
				this.reviewedBy &&
				this.value.trim() &&
				this.labDate.trim() &&
				this.timeSpentSeconds <= 7200 && // 2 hours in seconds
				this.selectedLabTestName.trim() &&
				this.selectedLabPanelGroup &&
				(this.selectedLabPanelGroup !== 'Other' || this.otherLabPanelGroup.trim()) &&
				this.selectedLabSpecimenType &&
				(this.selectedLabSpecimenType !== 'Other' || this.otherLabSpecimenType.trim()) &&
				this.selectedLabUnit &&
				(this.selectedLabUnit !== 'Other' || this.otherLabUnit.trim()) &&
				this.selectedLabInterpretation &&
				(this.selectedLabInterpretation !== 'Other' || this.otherLabInterpretation.trim())
			);
		},

		formattedDate () {
			return this.formatTimestamp(this.labDate);
		},
	},

	methods: {
		...mapActions({
			getLabPanelGroupTemplate: 'ccmRecords/getLabPanelGroupTemplate',
			getLabSpecimenTypeTemplate: 'ccmRecords/getLabSpecimenTypeTemplate',
			getLabInterpretationTemplate: 'ccmRecords/getLabInterpretationTemplate',
			getLabUnitTemplate: 'ccmRecords/getLabUnitTemplate',
			getLabCriticalFlagTemplate: 'ccmRecords/getLabCriticalFlagTemplate',
			addCCMLab: 'ccmLabs/addCCMLab',
		}),
		getRowsForDataTable,
		formatTimestamp,

		closeDrawer () {
			this.timeSpentSeconds = 0;
			this.reviewedBy = null;
			this.value = '';
			this.isCritical = false;
			this.relatedDiagnosisCode = '';
			this.selectedLabTestName = '';
			this.selectedLabPanelGroup = '';
			this.selectedLabSpecimenType = '';
			this.selectedLabInterpretation = '';
			this.selectedLabUnit = '';
			this.otherLabPanelGroup = '';
			this.otherLabSpecimenType = '';
			this.otherLabInterpretation = '';
			this.otherLabUnit = '';
			this.$store.commit('SET_DETAILS_DRAWER', false);
			this.key = !this.key;
		},

		async addLab () {
			const body = {
				ccmRecordId: this.currentRecord.id,
				patientId: this.currentRecord.patientId,
				hospitalId: this.currentRecord.hospitalId,
				labDate: new Date(this.labDate).toISOString(),
				testName: this.selectedLabTestName,
				value: this.value,
				unit: this.selectedLabUnit === 'Other' ? this.otherLabUnit : this.selectedLabUnit,
				specimenType:
					this.selectedLabSpecimenType === 'Other' ? this.otherLabSpecimenType : this.selectedLabSpecimenType,
				isCritical: this.isCritical,
				panelGroup: this.selectedLabPanelGroup === 'Other' ? this.otherLabPanelGroup : this.selectedLabPanelGroup.name,
				interpretation:
					this.selectedLabInterpretation === 'Other' ? this.otherLabInterpretation : this.selectedLabInterpretation,
				staffId: this.reviewedBy.id,
				performedByRole: this.roleList.find((item) => item.id === this.reviewedBy.roleId).roleName,
				reviewedBy: this.reviewedBy.fullName,
				reviewedAt: new Date().toISOString(),
				timeSpentSeconds: this.timeSpentSeconds,
				relatedDiagnosisCode: this.relatedDiagnosisCode,
				additionalData: '',
			};
			this.addCCMLab(body);
			this.closeDrawer();
		},

		filterMedicalStaff () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList
				.filter((member) => (member.roleId === 3 || member.roleId === 4) && member.isSequoiaUser && member.status === 1)
				.map((member) => ({
					...member,
					fullName: `${member.firstName} ${member.lastName}`,
				}));
		},

		filterNumbersOnly (inputEvent) {
			if (/\D/.test(inputEvent.key)) {
				inputEvent.preventDefault();
			}
			return true;
		},
	},
	async mounted () {
		this.medicalStaff = this.filterMedicalStaff();

		const panelGroupTemplate = await this.getLabPanelGroupTemplate();
		this.labPanelGroupTemplate = panelGroupTemplate.data;

		const specimenTypeTemplate = await this.getLabSpecimenTypeTemplate();
		this.labSpecimenTypeTemplate = specimenTypeTemplate.data;

		const interpretationTemplate = await this.getLabInterpretationTemplate();
		this.labInterpretationTemplate = interpretationTemplate.data;

		const unitTemplate = await this.getLabUnitTemplate();
		this.labUnitTemplate = unitTemplate.data;

		// const criticalFlagTemplate = await this.getLabCriticalFlagTemplate();
		// this.labCriticalFlagTemplate = criticalFlagTemplate.data;

		// const sourceTemplate = await this.getLabSourceTemplate();
		// this.labSourceTemplate = sourceTemplate.data;
	},
};
</script>
